'use client'

import { useCallback, useEffect, useState } from 'react'
import {
  getLocalStorageObjectKey,
  getLocalStorageValue,
  setLocalStorageObjectKey,
  setLocalStorageValue,
  StorageObjectKeyOptions,
  StorageOptions,
} from '~/utils/localStorageClient'

export const useLocalStorage = <T>(
  options: StorageOptions,
): [T, (value: T) => void] => {
  const { key, initialValue } = options

  const [localState, setLocalState] = useState<T>(() =>
    getLocalStorageValue({ key, initialValue }),
  )

  const handleSetState = useCallback(
    (value: T) => {
      setLocalState(value)
      setLocalStorageValue({ key, initialValue }, value)
    },
    [key, initialValue],
  )

  useEffect(() => {
    function handleStorageChange(event: StorageEvent) {
      if (event.key === key && event.newValue) {
        setLocalState(JSON.parse(event.newValue))
      }
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('storage', handleStorageChange)
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('storage', handleStorageChange)
      }
    }
  }, [key])

  return [localState, handleSetState as (value: T) => void]
}

export const useLocalStorageObjectKey = <T>(
  options: StorageObjectKeyOptions,
): [T, (value: T) => void] => {
  const [value, setValue] = useState<T>(() => getLocalStorageObjectKey(options))

  useEffect(() => {
    setLocalStorageObjectKey(options, value)
  }, [options, value])

  return [value, setValue as (value: T) => void]
}
